import React, { useState } from 'react';

/**
 * Structured, collapsible rendering of a parsed JSON value for the tool-call
 * detail modal. Objects and arrays get a toggle caret and per-type coloring;
 * primitives render inline. Everything is expanded by default — the modal's
 * scroll container bounds the height.
 */

/** True when the value renders as a collapsible container (object/array) */
function isContainer(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}

const PrimitiveValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null) return <span className="json-view-null">null</span>;
  switch (typeof value) {
    case 'string':
      return <span className="json-view-string">"{value}"</span>;
    case 'number':
      return <span className="json-view-number">{String(value)}</span>;
    case 'boolean':
      return <span className="json-view-boolean">{String(value)}</span>;
    default:
      // undefined / functions can't appear in parsed JSON; JSON.stringify
      // drops them from objects, so this is a defensive fallback only
      return <span className="json-view-null">{String(value)}</span>;
  }
};

const JsonNode: React.FC<{
  value: unknown;
  /** Object key or array index label; absent on the root node */
  name?: string;
}> = ({ value, name }) => {
  const [collapsed, setCollapsed] = useState(false);

  const label =
    name !== undefined ? (
      <>
        <span className="json-view-key">{name}</span>
        <span className="json-view-punct">: </span>
      </>
    ) : null;

  if (!isContainer(value)) {
    return (
      <div className="json-view-row">
        {label}
        <PrimitiveValue value={value} />
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray
    ? (value as unknown[]).map(
        (item, index) => [String(index), item] as [string, unknown],
      )
    : Object.entries(value as Record<string, unknown>);
  const [open, close] = isArray ? ['[', ']'] : ['{', '}'];
  const countLabel = `${entries.length} ${
    isArray
      ? entries.length === 1
        ? 'item'
        : 'items'
      : entries.length === 1
        ? 'key'
        : 'keys'
  }`;

  return (
    <div className="json-view-row">
      <button
        type="button"
        className="json-view-toggle"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((current) => !current)}>
        {collapsed ? '▸' : '▾'}
      </button>
      {label}
      <span className="json-view-punct">{open}</span>
      {collapsed ? (
        <span className="json-view-collapsed-note">
          {' '}
          {countLabel} <span className="json-view-punct">{close}</span>
        </span>
      ) : entries.length === 0 ? (
        <span className="json-view-punct">{close}</span>
      ) : (
        <>
          <div className="json-view-children">
            {entries.map(([key, child]) => (
              <JsonNode key={key} name={key} value={child} />
            ))}
          </div>
          <span className="json-view-punct">{close}</span>
        </>
      )}
    </div>
  );
};

const JsonView: React.FC<{ value: unknown }> = ({ value }) => (
  <div className="json-view" data-testid="json-view">
    <JsonNode value={value} />
  </div>
);

export default JsonView;
